import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MccCode, mccLabel } from './mccCodes';

// Selector de MCC con buscador (por código o descripción, sin importar tildes ni mayúsculas)
type MccSelectProps = {
    codes: MccCode[];
    value: string;                  // código seleccionado
    onChange: (code: string) => void;
    label?: string;
    invalid?: boolean;
};

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const MccSelect: React.FC<MccSelectProps> = ({ codes, value, onChange, label, invalid }) => {
    const selected = codes.find(c => c.code === value);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const boxRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const results = useMemo(() => {
        const terms = normalize(query).split(/\s+/).filter(Boolean);
        if (!terms.length) return codes;
        return codes.filter(c => {
            const text = normalize(mccLabel(c));
            return terms.every(t => text.includes(t));
        });
    }, [codes, query]);

    // Cerrar al hacer clic fuera
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    useEffect(() => { setActive(0); }, [query]);

    useEffect(() => {
        listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
    }, [active]);

    const choose = (mcc: MccCode) => {
        onChange(mcc.code);
        setQuery('');
        setOpen(false);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, results.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); if (open && results[active]) choose(results[active]); }
        else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    };

    return (
        <div ref={boxRef} className="position-relative">
            {label && <label htmlFor="mcc" className="form-label small text-muted mb-1">{label}</label>}
            <div className="input-group has-validation">
                <span className="input-group-text"><i className="bi bi-search" /></span>
                <input
                    id="mcc"
                    type="text"
                    className={`form-control${invalid ? ' is-invalid' : ''}`}
                    placeholder={selected ? mccLabel(selected) : 'Busque por código o actividad (ej. 5812 o restaurante)'}
                    value={open ? query : (selected ? mccLabel(selected) : '')}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls="mcc-list"
                    aria-autocomplete="list"
                />
                <div className="invalid-feedback">Seleccione el MCC</div>
            </div>
            {open && (
                <ul id="mcc-list" ref={listRef} role="listbox"
                    className="list-group position-absolute w-100 shadow-sm mt-1"
                    style={{ zIndex: 1050, maxHeight: 280, overflowY: 'auto' }}>
                    {results.length === 0 && (
                        <li className="list-group-item small text-muted">Sin resultados para "{query}"</li>
                    )}
                    {results.map((mcc, i) => (
                        <li
                            key={mcc.code}
                            role="option"
                            aria-selected={mcc.code === value}
                            className={`list-group-item list-group-item-action small${i === active ? ' active' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onMouseDown={(e) => { e.preventDefault(); choose(mcc); }}
                            onMouseEnter={() => setActive(i)}
                        >
                            <strong className="me-2">{mcc.code}</strong>{mcc.description}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default MccSelect;
